// CRUD do checklist da viagem.
// Sub-coleção: `trips/{tripId}/checklist/{itemId}`
//
// Schema:
//   { description: string, dueDate: 'YYYY-MM-DD' | null, done: bool,
//     createdAt: Timestamp, updatedAt: Timestamp }

import {
  collection, doc, getDocs, setDoc, deleteDoc, updateDoc,
  serverTimestamp, query, orderBy,
} from 'firebase/firestore';
import { db } from '../../core/firebase.js';
import { commitWrite } from '../../core/offlineWrite.js';

/** Lista todos os itens do checklist da viagem, ordenados por dueDate ASC. */
export async function listChecklist(tripId) {
  if (!tripId) return [];
  const q = query(
    collection(db, 'trips', tripId, 'checklist'),
    orderBy('dueDate', 'asc'),
  );
  try {
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn('[checklist] list failed:', err.message);
    return [];
  }
}

/** Cria item novo. Retorna { id, ...data }. */
export async function createChecklistItem(tripId, { description, dueDate }) {
  const ref = doc(collection(db, 'trips', tripId, 'checklist'));
  const payload = {
    description: String(description || '').trim(),
    dueDate: dueDate || null,
    done: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  await commitWrite(setDoc(ref, payload));
  notifyChanged();
  return { id: ref.id, ...payload };
}

/**
 * Atualiza item (parcial).
 *
 * Quando a `dueDate` entra no patch, as notificações derivadas do item mudam
 * de id (o id inclui a data — ver notifications/engine.js). As dispensas
 * gravadas pros ids antigos ficariam órfãs em `users/{uid}.notifications
 * .dismissedIds`, então avisamos a central pra esquecê-las.
 */
export async function updateChecklistItem(tripId, itemId, patch) {
  await commitWrite(updateDoc(doc(db, 'trips', tripId, 'checklist', itemId), {
    ...patch,
    updatedAt: serverTimestamp(),
  }));
  if (patch && 'dueDate' in patch) forgetNotifications(tripId, itemId);
  notifyChanged();
}

export async function deleteChecklistItem(tripId, itemId) {
  await commitWrite(deleteDoc(doc(db, 'trips', tripId, 'checklist', itemId)));
  forgetNotifications(tripId, itemId);
  notifyChanged();
}

// Avisa a central de notificações (se montada) pra recalcular/reagendar.
// Global no-op: não cria dependência estática com a feature notifications.
function notifyChanged() {
  try { window.mpNotifRefresh?.(); } catch { /* noop */ }
}

// Descarta as dispensas de TODAS as notificações desse item do checklist
// (o id derivado é `checklist:{tripId}:{itemId}:{dueDate}:{kind}`).
function forgetNotifications(tripId, itemId) {
  try { window.mpNotifForget?.(`checklist:${tripId}:${itemId}:`); } catch { /* noop */ }
}
